import { countBy, pick } from "lodash"
import { GOALS } from "./dataStore"

const MIN_WEIGHT = 30
const MAX_WEIGHT = 300

const MIN_HEIGHT = 100
const MAX_HEIGHT = 250

const MIN_AGE = 18
const MAX_AGE = 100

const isString = (value) => typeof value === "string"
const isNotEmptyString = (value) => !!value.trim()
const exists = (value) => value !== null

const countInString = (str, ch) => countBy(str)[ch] || 0

export const validators = {
  measurementSystem: (value) => ["imperial", "metric"].includes(value),
  gender: (value) => ["female", "male"].includes(value),
  goal: (value) => Object.values(GOALS).includes(value),
  email: (value) =>
    isString(value) && value.match(/^\S+@\S+\.\S{2,}$/) && countInString(value, "@") === 1,
  age: (value) => exists(value) && Number(value) >= MIN_AGE && Number(value) <= MAX_AGE,
  targetWeightKg: (value) => !!Number(value),
  targetWeightLbs: (value) => !!Number(value),
  firstName: (value) => isString(value) && isNotEmptyString(value),
  weightKg: (value) => exists(value) && Number(value) >= MIN_WEIGHT && Number(value) <= MAX_WEIGHT,
  weightLbs: (value) => {
    if (!exists(value)) return false

    const weight = Math.round(Number(value) * 0.453592)

    return weight >= MIN_WEIGHT && weight <= MAX_WEIGHT
  },
  heightCm: (value) => exists(value) && Number(value) >= MIN_HEIGHT && Number(value) <= MAX_HEIGHT,
  heightFt: (value, { heightFt, heightIn }) => {
    if (!exists(value)) return false

    const height = Math.ceil(Number(heightFt) * 30.48 + Number(heightIn) * 2.54)

    return height >= MIN_HEIGHT && height <= MAX_HEIGHT
  },
  heightIn: (value, { heightFt, heightIn }) => {
    if (!exists(value) || value > 12) return false

    const height = Math.ceil(Number(heightFt) * 30.48 + Number(heightIn) * 2.54)

    return height >= MIN_HEIGHT && height <= MAX_HEIGHT
  },
  apetite: exists,
  ratherEat: exists,
  smoking: exists,
  likeBetter: exists,
  beverageLikeBetter: exists,
  feelBloated: exists,
  firstMealAfter: exists,
  hormonalContraceptions: (value, { gender }) => (gender === "female" ? exists(value) : true),
  bonePain: (value, { gender }) => (gender === "male" ? exists(value) : true),
  fallingAsleep: exists,
  eatingMeatOften: exists,
  likeBetter2: exists,
  hiccups: exists,
  childrenUpTo18: (value, { gender }) => (gender === "female" ? exists(value) : true),
  alcoholConsumption: (value, { gender }) => (gender === "male" ? exists(value) : true),
  wantToLoseWeight: exists,
  milkAllergy: exists,
  privacyAgreement: (value) => value === "Yes",
  weightLossReceive: exists,
}

export const ERROR_MESSAGES = new Proxy(
  {
    weightKg: "Weight entered incorrectly",
    weightLbs: "Weight entered incorrectly",
    age: "Years entered incorrectly",
    heightCm: "Invalid height entered",
    heightFt: "Invalid height entered",
    heightIn: "Invalid height entered",
    firstName: "Name entered incorrectly",
    apetite: "Please select an answer to continue",
    ratherEat: "Please select an answer to continue",
    smoking: "Please select an answer to continue",
    likeBetter: "Please select an answer to continue",
    beverageLikeBetter: "Please select an answer to continue",
    feelBloated: "Please select an answer to continue",
    firstMealAfter: "Please select an answer to continue",
    hormonalContraceptions: "Please select an answer to continue",
    bonePain: "Please select an answer to continue",
    fallingAsleep: "Please select an answer to continue",
    eatingMeatOften: "Please select an answer to continue",
    likeBetter2: "Please select an answer to continue",
    hiccups: "Please select an answer to continue",
    childrenUpTo18: "Please select an answer to continue",
    alcoholConsumption: "Please select an answer to continue",
    wantToLoseWeight: "Please select an answer to continue",
    milkAllergy: "Please select an answer to continue",
    privacyAgreement: "Please select an answer to continue",
    weightLossReceive: "Please select an answer to continue",
    email: "Please enter your email",
  },
  {
    get: (target, name) => (target.hasOwnProperty(name) ? target[name] : "invalid"),
  }
)

const validate = (data, fields) => {
  const errors = {}
  let hasErrors = false

  Object.entries(fields ? pick(data, fields) : data).forEach(([field, value]) => {
    const validator = validators[field]

    if (validator && !validator(value, data)) {
      hasErrors = true
      errors[field] = ERROR_MESSAGES[field]
    }
  })

  return { hasErrors, errors }
}

export default validate
